import React, { useCallback, useEffect, useState } from "react";
import globalAxios from "axios";
import { FiActivity, FiRefreshCw, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import type { LogEntry } from "../../API/Models";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import { ActivityRow, ActivityDetailModal } from "./ActivityFeed";

type Target = "preview" | "production";

const PAGE_SIZE = 30;

type Page = { items: LogEntry[]; total: number; page: number; pageSize: number };

/**
 * « Activité » : the deployment page's recent-logs panel. Shows the system-log events for ONE
 * environment (preview / production, production selected by default), server-paginated at 30 per page
 * via GET /api/logs/paged?tags=<target>. Rows + detail modal are shared with the live ActivityFeed.
 */
const DeploymentActivity: React.FC = () => {
	const [target, setTarget] = useState<Target>("production");
	const [page, setPage] = useState(1);
	const [data, setData] = useState<Page>({ items: [], total: 0, page: 1, pageSize: PAGE_SIZE });
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [selected, setSelected] = useState<LogEntry | null>(null);

	const load = useCallback(
		async (p: number, t: Target) => {
			setLoading(true);
			setError(null);
			try {
				const res = await globalAxios.get(`/api/logs/paged`, {
					withCredentials: true,
					headers: { Accept: "application/json" },
					params: { tags: t, page: p, pageSize: PAGE_SIZE, includeDebug: false }
				});
				const d = res.data ?? {};
				setData({
					items: Array.isArray(d.items) ? d.items : [],
					total: Number(d.total ?? 0),
					page: Number(d.page ?? p),
					pageSize: Number(d.pageSize ?? PAGE_SIZE)
				});
			} catch (err) {
				console.error(err);
				setError("Impossible de charger l'activité.");
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	useEffect(() => {
		load(page, target);
	}, [load, page, target]);

	// Switching environment resets to the first page.
	const switchTarget = (t: Target) => {
		if (t === target) return;
		setTarget(t);
		setPage(1);
	};

	const totalPages = Math.max(1, Math.ceil(data.total / (data.pageSize || PAGE_SIZE)));

	return (
		<section className="flex min-h-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
			<header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
				<div className="flex items-center gap-2">
					<FiActivity className="text-slate-400" />
					<h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">Activité</h3>
					<span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
						<span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> auto
					</span>
				</div>
				<div className="flex items-center gap-2">
					{/* Environment selector (production selected by default). */}
					<div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-0.5">
						{(["preview", "production"] as const).map((tt) => (
							<button
								key={tt}
								type="button"
								onClick={() => switchTarget(tt)}
								className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
									target === tt ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
								}`}
							>
								{tt === "preview" ? "Preview" : "Production"}
							</button>
						))}
					</div>
					<button
						type="button"
						onClick={() => load(data.page, target)}
						className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
						title="Rafraîchir"
					>
						<FiRefreshCw className={loading ? "animate-spin" : ""} />
					</button>
				</div>
			</header>

			<div className="min-h-0 flex-1">
				{error && <p className="px-4 py-3 text-sm text-rose-600">{error}</p>}
				{!error && loading && data.items.length === 0 && (
					<div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-400">
						<LoadingSpinner size="sm" /> Chargement…
					</div>
				)}
				{!error && !loading && data.items.length === 0 && (
					<p className="px-4 py-10 text-center text-sm text-slate-400">Aucune activité pour {target === "production" ? "la production" : "la preview"}.</p>
				)}
				<ul className="divide-y divide-slate-100">
					{data.items.map((entry, i) => (
						<ActivityRow key={entry.id ?? `${entry.type}-${i}`} entry={entry} onSelect={setSelected} />
					))}
				</ul>
			</div>

			{data.total > 0 && (
				<div className="flex shrink-0 items-center justify-between border-t border-slate-100 bg-slate-50/60 px-4 py-2 text-xs text-slate-500">
					<span>
						Page {data.page} sur {totalPages} · {data.total} élément{data.total > 1 ? "s" : ""}
					</span>
					<div className="flex items-center gap-1">
						<button
							type="button"
							disabled={loading || data.page <= 1}
							onClick={() => setPage((p) => Math.max(1, p - 1))}
							className="inline-flex h-7 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
							title="Page précédente"
						>
							<FiChevronLeft /> Précédent
						</button>
						<button
							type="button"
							disabled={loading || data.page >= totalPages}
							onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
							className="inline-flex h-7 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
							title="Page suivante"
						>
							Suivant <FiChevronRight />
						</button>
					</div>
				</div>
			)}

			{selected && <ActivityDetailModal entry={selected} onClose={() => setSelected(null)} />}
		</section>
	);
};

export default DeploymentActivity;
